import { Grid, Stack, InputLabel } from '@mui/material';
import MultiSelect from 'react-select';
import Input from './Input';
import CustomButton from './Button';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import PropTypes from 'prop-types';
import { getAuthRequest } from 'Api/api';
import { style } from 'constant/constants';
import { useState, useEffect } from 'react';
import ErrorForSelect from './ErrorForSelect';
import { useSelector } from 'react-redux';
import { isAdmin, isAreaAdmin } from 'utils/utils';
import { ROLES } from 'constant/enums';
import Loader from 'components/Loader';
import StoreNameLabel from './StoreNameLabel';
import CustomDropdown from './CustomDropdown';

const InputFeilds = [
  {
    name: 'firstName',
    label: 'First Name',
    type: 'text',
    placeholder: 'Enter first name',
    maxLength: 50,
    required: true
  },

  {
    name: 'lastName',
    label: 'Last Name',
    type: 'text',
    maxLength: 50,
    placeholder: 'Enter last name',
    required: true
  },

  {
    name: 'email',
    label: 'Email',
    type: 'email',
    maxLength: 50,
    placeholder: 'Enter email',
    required: true
  }
];

const roleOptions = [
  { name: ROLES.ROLE_AREA_ADMIN, id: ROLES.ROLE_AREA_ADMIN },
  { name: ROLES.ROLE_SUPER_ADMIN, id: ROLES.ROLE_SUPER_ADMIN }
];

const AreaAdminForm = ({
  isEmpty,
  setIsEmpty,
  user,
  userInfo,
  setUserInfo,
  disableEmail,
  buttonText,
  buttonClose,
  handleSubmit,
  handleCancelAction,
  handleUserInfo,
  error,
  handleError,
  edit = false
}) => {
  // updating state for multi select
  const [role, setRole] = useState(edit ? ROLES[userInfo.role] : ROLES.ROLE_AREA_ADMIN);

  const [stores, setStores] = useState(
    user != null
      ? user?.storesList?.map((obj) => {
          return {
            value: obj.storeId,
            label: <StoreNameLabel active={obj?.active} storeName={obj?.storeName} />
          };
        })
      : []
  );

  const [showLoader, setShowLoader] = useState(false);
  const [storesList, setStoresList] = useState([]);

  const { userData, storeData, adminAccess } = useSelector((store) => store.auth);
  const getStoresList = async () => {
    try {
      setShowLoader(true);
      const storesList = await getAuthRequest('getStoresList');
      const list = storesList?.storesList
        .sort((store) => {
          return store.active ? -1 : 1;
        })
        .map((obj) => {
          return {
            value: obj.id,
            label: <StoreNameLabel active={obj?.active} storeName={obj?.storeName} />
          };
        });

      setStoresList(list);
      setShowLoader(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getStoresList();
  }, []);

  // setting role based store id
  useEffect(() => {
    if (isAdmin(userData.roles)) {
      setUserInfo((prevState) => {
        return { ...prevState, store: adminAccess && storeData?.id };
      });
    }
  }, [isAreaAdmin(userData.roles), isAdmin(userData.roles), adminAccess]);

  useEffect(() => {
    setUserInfo((prev) => ({
      ...prev,
      role,
      stores: role === ROLES.ROLE_SUPER_ADMIN ? [] : prev.stores
    }));
  }, [role]);

  // flag use for set the multi select field empty
  useEffect(() => {
    if (isEmpty) {
      setStores([]);
      setIsEmpty(!isEmpty);
    }
  }, [isEmpty]);

  const filterOption = (option, inputValue) => {
    const { label } = option;
    // looking if other options with same label are matching inputValue
    const otherKey = storesList.filter(
      (opt) =>
        opt?.label?.props?.storeName?.toLowerCase() == label?.props?.storeName?.toLowerCase() &&
        opt?.label?.props?.storeName?.toLowerCase().includes(inputValue?.toLowerCase())
    );

    return (
      option?.label?.props?.storeName?.toLowerCase().includes(inputValue?.toLowerCase()) ||
      otherKey.length > 0
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      {InputFeilds.map(({ name, type, placeholder, label, required, maxLength }) => (
        <Stack key={name} direction="row" sx={{ marginTop: '3vh' }}>
          <Input
            type={type}
            placeholder={placeholder}
            onChange={handleUserInfo}
            value={userInfo[name]}
            required={required}
            maxLength={maxLength}
            label={label}
            onFocus={() => handleError(name, null)}
            disabled={disableEmail && name === 'email'}
            name={name}
            error={error[name]}
          />
        </Stack>
      ))}
      <Grid item xs={12} sx={{ mx: '2vh', marginTop: '3vh' }}>
        <CustomDropdown
          disabled={edit}
          styles={{ width: '100%' }}
          name="role"
          label="Role"
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
            if (e.target.value === ROLES.ROLE_SUPER_ADMIN) {
              setStores([]);
              handleError('role', null);
            }
            setUserInfo((prev) => ({
              ...prev,
              role: e.target.value,
              stores: e.target.value === ROLES.ROLE_SUPER_ADMIN ? [] : prev.stores
            }));
          }}
          list={roleOptions}
          isId={true}
          isRequired={true}
          allowEmptySelection={true}
        />
        {error.role && (
          <ErrorForSelect style={{ marginTop: 8, marginLeft: 28 }}>{error.role}</ErrorForSelect>
        )}
      </Grid>
      {showLoader && <Loader open={showLoader} />}
      <Grid item xs={12} sx={{ ml: '5vh', mr: '5vh', marginTop: '3vh' }}>
        {/* stores field with all stores list - multi select - for Area Admin only */}
        {role === ROLES.ROLE_AREA_ADMIN && (
          <>
            <InputLabel
              htmlFor="demo-simple-select-label"
              sx={{ fontFamily: style.fontFamily, color: style.LIGHT.primary }}
              className="text-menu-color-green">
              Stores <span className="text-red-600">*</span>
            </InputLabel>
            <MultiSelect
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderColor: error.stores ? style.LIGHT.primary : ''
                })
              }}
              onFocus={() => handleError('stores', null)}
              sx={{ width: '100%', fontFamily: style.fontFamily, mt: 0, mb: -2 }}
              error={error.stores}
              menuShouldScrollIntoView={false}
              name="stores"
              defaultValue={stores}
              onChange={(selected) => {
                setStores(selected || []);
                setUserInfo((prev) => ({
                  ...prev,
                  stores: (selected || []).map((s) => s.value)
                }));
                handleError('stores', null);
              }}
              options={storesList}
              filterOption={filterOption}
              value={stores}
              isMulti
            />
            {error.stores && (
              <ErrorForSelect style={{ marginTop: 8 }}>{error.stores}</ErrorForSelect>
            )}
          </>
        )}
      </Grid>
      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ mr: '5vh' }}>
        <CustomButton
          sx={{ ml: '15vh' }}
          type="submit"
          onClick={handleSubmit}
          endIcon={buttonText === 'Save' ? <SaveIcon /> : <AddIcon />}>
          {buttonText}
        </CustomButton>
        {buttonText !== 'Save' && (
          <CustomButton type="button" endIcon={<SaveIcon />} onClick={handleCancelAction}>
            {buttonClose}
          </CustomButton>
        )}
      </Stack>
    </form>
  );
};

AreaAdminForm.propTypes = {
  isEmpty: PropTypes.bool,
  setIsEmpty: PropTypes.func,
  userInfo: PropTypes.object,
  setUserInfo: PropTypes.func,
  buttonText: PropTypes.string,
  buttonClose: PropTypes.string,
  handleSubmit: PropTypes.func,
  handleCancelAction: PropTypes.func,
  handleUserInfo: PropTypes.func,
  handleError: PropTypes.func,
  roles: PropTypes.array,
  disableEmail: PropTypes.bool,
  user: PropTypes.object,
  error: PropTypes.object,
  edit: PropTypes.bool
};

export default AreaAdminForm;
